import { makeStyles, MenuItem, Typography } from '@material-ui/core'
import { ArrowForward, Close } from '@material-ui/icons'
import React, { useState, useReducer, useEffect } from 'react'
import {
  useMutation,
  useNotify,
  useListContext,
  GetManyResult,
  GET_MANY,
  sanitizeFetchType,
  useTranslate,
} from 'react-admin'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import { Authority } from '../../../../core/auth/Authority'
import { BarrierSelectStateDialog } from '../BarrierDialogs'
import { ApiErrorDto } from '../../../../core/dto/error-handling/api-error.dto'
import { ResourceName } from '../../../../core/ResourceName'
import { BulkActionProps } from '../../../common/BulkActionsProps'
import {
  ProgressStatus,
  StateBulkProgressDialog,
} from '../../../common/StateBulkProgressDialog'
import { BarrierDto } from '../../../../core/dto/device/barrier/barrier.dto'
import { ToggleType } from '../../../../core/devices/barriers/barrier-set-boolean-status-button'
import {
  barrierSetFreePassButtonInitialState,
  barrierSetFreePassButtonReducer,
} from '../../../../core/devices/barriers/barrier-set-free-pass-button.reducer'

const useStyles = makeStyles({
  icon: {
    position: 'absolute',
    left: 5,
    top: 5,
    fontSize: '24px !important',
  },
  crossIcon: {
    position: 'absolute',
    left: 0,
    top: 0,
    fontSize: 34,
  },
})

export const BarrierSetFreePassBulkButton = ({
  selectedIds,
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  innerRef,
  asRecurringJob,
  ...props
}: BulkActionProps) => {
  const { loading, refetch } = useListContext<BarrierDto>()
  const hasAuthority = useHasAuthority()
  const notify = useNotify()
  const translate = useTranslate()
  const classes = useStyles()

  const [{ mutateQuery }, dispatch] = useReducer(
    barrierSetFreePassButtonReducer,
    barrierSetFreePassButtonInitialState,
  )
  const [selectStateDialogOpen, setSelectStateDialogOpen] = useState(false)
  const [mutate, { loading: mutationLoading }] = useMutation()
  const [selectedState, setSelectedState] = useState(false)
  const [bulkStateDialogOpen, setBulkStateDialogOpen] = useState(false)
  const [bulkProgressStatus, setBulkProgressStatus] = useState<
    ProgressStatus[]
  >([])
  const updateBulkProgressStatus = (newStatus: ProgressStatus) => {
    const oldStatusIndex = bulkProgressStatus.findIndex(
      (s) => s.id === newStatus.id,
    )
    bulkProgressStatus.splice(oldStatusIndex, 1, newStatus)
  }
  const [selectedBarriers, setSelectedBarriers] = useState<BarrierDto[]>([])

  const handleMutation = async (
    barrier: BarrierDto,
    recurringJobName?: string,
    cronExpression?: string,
    auditNote?: string,
  ) => {
    let error
    let query
    if (!asRecurringJob) {
      query = mutateQuery?.(barrier, auditNote)
    } else {
      query = mutateQuery?.(
        barrier,
        auditNote,
        recurringJobName,
        cronExpression,
      )
    }
    let result = null
    try {
      result = await mutate(query, {
        returnPromise: true,
      })
    } catch (err) {
      error = err
    }
    if (error) {
      updateBulkProgressStatus({
        id: barrier.id,
        status: 'error',
        error: error as ApiErrorDto,
      } as ProgressStatus)
      return error
    }
    updateBulkProgressStatus({
      id: barrier.id,
      status: 'success',
    } as ProgressStatus)
    return result
  }

  const handleSetFreePass = async (
    recurringJobName?: string,
    cronExpression?: string,
    auditNote?: string,
  ) => {
    setBulkStateDialogOpen(true)
    const pendingRequests: Promise<any>[] = []
    const selectedBarriersResponse = (await mutate(
      {
        type: sanitizeFetchType(GET_MANY),
        resource: ResourceName.BARRIERS,
        payload: {
          ids: selectedIds,
        },
      },
      {
        returnPromise: true,
        onFailure: (err) => {
          notify(err?.message, 'error')
          setBulkStateDialogOpen(false)
        },
      },
    )) as GetManyResult<BarrierDto>
    if (!selectedBarriersResponse.data.length) {
      setBulkStateDialogOpen(false)
      return
    }
    selectedBarriersResponse.data.forEach((barrier: BarrierDto) => {
      pendingRequests.push(
        handleMutation(barrier, recurringJobName, cronExpression, auditNote),
      )
      selectedBarriers.push(barrier)
      bulkProgressStatus.push({
        id: barrier.id,
        status: 'pending',
      } as ProgressStatus)
    })
    await Promise.all(pendingRequests)
    refetch()
  }

  useEffect(() => {
    if (selectedState) {
      dispatch({ type: ToggleType.OFF })
    } else {
      dispatch({ type: ToggleType.ON })
    }
  }, [selectedState])

  useEffect(() => {
    if (!bulkStateDialogOpen) {
      setBulkProgressStatus([])
      setSelectedBarriers([])
    }
  }, [bulkStateDialogOpen])

  return (
    <MenuItem
      {...props}
      disabled={
        !selectedIds.length ||
        !hasAuthority(Authority.BARRIERS_SET_FREE_PASS_BUTTON) ||
        loading ||
        mutationLoading
      }
      onClick={() => setSelectStateDialogOpen(true)}
      button
    >
      <Typography>
        {translate(
          !asRecurringJob
            ? 'resources.barriers.actions.setFreePassState.set'
            : 'resources.barriers.actions.setFreePassState.set-recurring',
        )}
      </Typography>
      <BarrierSelectStateDialog
        open={selectStateDialogOpen}
        confirm={handleSetFreePass}
        method="freePass"
        close={() => setSelectStateDialogOpen(false)}
        state={selectedState}
        setState={setSelectedState}
        asRecurringJob={asRecurringJob}
        OnIcon={<ArrowForward className={classes.icon} />}
        OffIcon={
          <>
            <ArrowForward className={classes.icon} />
            <Close fontSize="large" className={classes.crossIcon} />
          </>
        }
        withAuditNote
      />
      <StateBulkProgressDialog
        open={bulkStateDialogOpen}
        close={() => setBulkStateDialogOpen(false)}
        elements={selectedBarriers}
        method="freePass"
        statuses={bulkProgressStatus}
        resource={ResourceName.BARRIERS}
      />
    </MenuItem>
  )
}
